/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  CreateWebhookEndpointDto,
  CreatedWebhookEndpointDto,
  WebhookDeliveryDto,
  WebhookEndpointDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary The workspace's webhook endpoints, newest first, each with its latest delivery.
 */
export const listWebhookEndpoints = (id: string, signal?: AbortSignal) => {
  return customInstance<WebhookEndpointDto[]>({
    url: `/api/v1/workspaces/${id}/webhook-endpoints`,
    method: 'GET',
    signal,
  });
};

export const getListWebhookEndpointsQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/webhook-endpoints`] as const;
};

export const getListWebhookEndpointsQueryOptions = <
  TData = Awaited<ReturnType<typeof listWebhookEndpoints>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWebhookEndpoints>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListWebhookEndpointsQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebhookEndpoints>>> = ({
    signal,
  }) => listWebhookEndpoints(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listWebhookEndpoints>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListWebhookEndpointsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listWebhookEndpoints>>
>;
export type ListWebhookEndpointsQueryError = ErrorType<ApiErrorDto>;

export function useListWebhookEndpoints<
  TData = Awaited<ReturnType<typeof listWebhookEndpoints>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWebhookEndpoints>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWebhookEndpoints>>,
          TError,
          Awaited<ReturnType<typeof listWebhookEndpoints>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWebhookEndpoints<
  TData = Awaited<ReturnType<typeof listWebhookEndpoints>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWebhookEndpoints>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWebhookEndpoints>>,
          TError,
          Awaited<ReturnType<typeof listWebhookEndpoints>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWebhookEndpoints<
  TData = Awaited<ReturnType<typeof listWebhookEndpoints>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWebhookEndpoints>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The workspace's webhook endpoints, newest first, each with its latest delivery.
 */

export function useListWebhookEndpoints<
  TData = Awaited<ReturnType<typeof listWebhookEndpoints>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWebhookEndpoints>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListWebhookEndpointsQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Add an endpoint. The response is the only time its signing secret is shown.
 */
export const createWebhookEndpoint = (
  id: string,
  createWebhookEndpointDto: BodyType<CreateWebhookEndpointDto>,
  signal?: AbortSignal,
) => {
  return customInstance<CreatedWebhookEndpointDto>({
    url: `/api/v1/workspaces/${id}/webhook-endpoints`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createWebhookEndpointDto,
    signal,
  });
};

export const getCreateWebhookEndpointMutationKey = () =>
  ['createWebhookEndpoint'] as const;

export const getCreateWebhookEndpointMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createWebhookEndpoint>>,
    TError,
    CreateWebhookEndpointMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createWebhookEndpoint>>,
  TError,
  CreateWebhookEndpointMutationVariables,
  TContext
> => {
  const mutationKey = getCreateWebhookEndpointMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createWebhookEndpoint>>,
    CreateWebhookEndpointMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return createWebhookEndpoint(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateWebhookEndpointMutationResult = NonNullable<
  Awaited<ReturnType<typeof createWebhookEndpoint>>
>;
export type CreateWebhookEndpointMutationBody = BodyType<CreateWebhookEndpointDto>;
export type CreateWebhookEndpointMutationError = ErrorType<ApiErrorDto>;
export type CreateWebhookEndpointMutationVariables = {
  id: string;
  data: BodyType<CreateWebhookEndpointDto>;
};

/**
 * @summary Add an endpoint. The response is the only time its signing secret is shown.
 */
export const useCreateWebhookEndpoint = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createWebhookEndpoint>>,
      TError,
      CreateWebhookEndpointMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createWebhookEndpoint>>,
  TError,
  CreateWebhookEndpointMutationVariables,
  TContext
> => {
  return useMutation(getCreateWebhookEndpointMutationOptions(options), queryClient);
};
/**
 * @summary Remove an endpoint; it gets nothing more.
 */
export const deleteWebhookEndpoint = (
  id: string,
  endpointId: string,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/webhook-endpoints/${endpointId}`,
    method: 'DELETE',
    signal,
  });
};

export const getDeleteWebhookEndpointMutationKey = () =>
  ['deleteWebhookEndpoint'] as const;

export const getDeleteWebhookEndpointMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteWebhookEndpoint>>,
    TError,
    DeleteWebhookEndpointMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteWebhookEndpoint>>,
  TError,
  DeleteWebhookEndpointMutationVariables,
  TContext
> => {
  const mutationKey = getDeleteWebhookEndpointMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteWebhookEndpoint>>,
    DeleteWebhookEndpointMutationVariables
  > = (props) => {
    const { id, endpointId } = props ?? {};

    return deleteWebhookEndpoint(id, endpointId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteWebhookEndpointMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteWebhookEndpoint>>
>;

export type DeleteWebhookEndpointMutationError = ErrorType<ApiErrorDto>;
export type DeleteWebhookEndpointMutationVariables = { id: string; endpointId: string };

/**
 * @summary Remove an endpoint; it gets nothing more.
 */
export const useDeleteWebhookEndpoint = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteWebhookEndpoint>>,
      TError,
      DeleteWebhookEndpointMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteWebhookEndpoint>>,
  TError,
  DeleteWebhookEndpointMutationVariables,
  TContext
> => {
  return useMutation(getDeleteWebhookEndpointMutationOptions(options), queryClient);
};
/**
 * @summary An endpoint's latest 50 deliveries, newest first.
 */
export const listWebhookDeliveries = (
  id: string,
  endpointId: string,
  signal?: AbortSignal,
) => {
  return customInstance<WebhookDeliveryDto[]>({
    url: `/api/v1/workspaces/${id}/webhook-endpoints/${endpointId}/deliveries`,
    method: 'GET',
    signal,
  });
};

export const getListWebhookDeliveriesQueryKey = (id: string, endpointId: string) => {
  return [`/api/v1/workspaces/${id}/webhook-endpoints/${endpointId}/deliveries`] as const;
};

export const getListWebhookDeliveriesQueryOptions = <
  TData = Awaited<ReturnType<typeof listWebhookDeliveries>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  endpointId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWebhookDeliveries>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListWebhookDeliveriesQueryKey(id, endpointId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listWebhookDeliveries>>> = ({
    signal,
  }) => listWebhookDeliveries(id, endpointId, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      id !== null && id !== undefined && endpointId !== null && endpointId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listWebhookDeliveries>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListWebhookDeliveriesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listWebhookDeliveries>>
>;
export type ListWebhookDeliveriesQueryError = ErrorType<ApiErrorDto>;

export function useListWebhookDeliveries<
  TData = Awaited<ReturnType<typeof listWebhookDeliveries>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  endpointId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWebhookDeliveries>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWebhookDeliveries>>,
          TError,
          Awaited<ReturnType<typeof listWebhookDeliveries>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWebhookDeliveries<
  TData = Awaited<ReturnType<typeof listWebhookDeliveries>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  endpointId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWebhookDeliveries>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWebhookDeliveries>>,
          TError,
          Awaited<ReturnType<typeof listWebhookDeliveries>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWebhookDeliveries<
  TData = Awaited<ReturnType<typeof listWebhookDeliveries>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  endpointId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWebhookDeliveries>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary An endpoint's latest 50 deliveries, newest first.
 */

export function useListWebhookDeliveries<
  TData = Awaited<ReturnType<typeof listWebhookDeliveries>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  endpointId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listWebhookDeliveries>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListWebhookDeliveriesQueryOptions(id, endpointId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}
